import { formatExistingOpeningsOutline } from "./jobOpeningImportUtils";

/**
 * Copy-paste instructions for LLMs building Promang HR job-opening JSON.
 */
export function buildJobOpeningAiGuide(existingOpenings = []) {
  const existing = formatExistingOpeningsOutline(existingOpenings);

  return `You generate **Promang HR job opening JSON** for Human resources → Ledige stillinger → JSON / AI.

## Output rules
- Return **one** JSON object only:
  \`{ "atomic": false, "openings": [ ... ] }\`
- No markdown fences, no commentary before or after the JSON.
- Do **not** include database \`id\`, \`company_id\`, \`created_by_user_id\`, \`updated_by_user_id\`, \`deleted\`, \`published_at\` or \`closed_at\`.
- Upsert by \`slug\` within the company. Missing slug creates a new opening.
- Import never deletes openings that are not in the JSON.
- Default \`status\` for new openings: \`draft\`.
- Max 50 openings.
- Do **not** recreate existing openings unless the user asks to update them.

## Existing openings (do NOT recreate these unless the user asks to update)

${existing}

## Opening fields

title (required), slug, short_description, description, responsibilities, requirement_items[], requirements, benefits, location, employment_type (full_time|part_time|temporary|internship|freelance), department_name, salary_info, work_percentage, positions_count, application_deadline (YYYY-MM-DD), starts_on, status (draft|published|closed|archived), show_on_website, sort_order, applications[].

Do **not** include \`contact_name\`, \`contact_email\` or \`contact_phone\`. Do not invent a contact person. The opening contact is chosen as an employee in Promang. Omit contact unless the user gave a real company \`contact_user_id\`.

## Requirements checklist (not free text only)

Prefer \`requirement_items\` as a checklist hash:

\`[{ "id": "req_license", "text": "Førerkort klasse B" }]\`

- \`id\`: slug-style (\`req_license\`, \`req_fagbrev\`). Do **not** invent UUIDs. The API fills \`req_<hex>\` if id is missing.
- Also include \`requirements\` as synced bullet text of the **same** lines (\`- Punkt\\n- Punkt\`). Import keeps the fields in sync; \`requirement_items\` wins when both are set.
- Do not use \`requirements\` alone when you can generate \`requirement_items\`.

## Optional applications[] / candidates[]

first_name, last_name, email, phone, city, cover_letter, linkedin_url, status (new|reviewing|interview|offer|hired|rejected|withdrawn), source (manual|website|email|other), notes, rating (1-5), available_from, consent_privacy (true), requirement_checks[].

Optional \`requirement_checks\` must match the opening item ids:

\`[{ "id": "req_license", "text": "Førerkort klasse B", "checked": true }]\`

- Same \`id\` + \`text\` as the opening \`requirement_items\`.
- Do not invent UUIDs or extra checklist ids that are not on the opening.

## Sample

{
  "atomic": false,
  "openings": [
    {
      "title": "Servicetekniker ventilasjon",
      "slug": "servicetekniker-ventilasjon",
      "short_description": "Kort ingress til nettside og liste",
      "description": "Full stillingstekst i bokmål.",
      "responsibilities": "- Serviceavtaler\\n- Feilsøking",
      "requirement_items": [
        { "id": "req_license", "text": "Førerkort klasse B" },
        { "id": "req_fagbrev", "text": "Fagbrev eller tilsvarende" }
      ],
      "requirements": "- Førerkort klasse B\\n- Fagbrev eller tilsvarende",
      "benefits": "- Servicebil",
      "location": "Oslo",
      "employment_type": "full_time",
      "status": "draft",
      "show_on_website": false,
      "applications": []
    }
  ]
}

Write professional bokmål unless another language is requested. Output **JSON only**.`;
}
